/**
 * @file chat-row-view.ts
 * @description Модель строки списка диалогов (chat-window-design.md §2):
 * время, превью с автором, контекст, пометка по приоритету.
 *
 * Приоритет пометки справа в третьей строке — реестром `MARKER_RULES`
 * (первое сработавшее правило): «Передан вам» → «Из архива» → «Без
 * ответственного» → «ведёт: …». Признаки берём у сервера (`flags`): они
 * личные и гаснут, когда этот сотрудник открыл диалог (`mark-read`, R4.19) —
 * кабинет «открыл» у себя не хранит. Порядок строк — как отдал сервер (по
 * последнему событию, R4.18), время в строке — `lastActivityAt`. Счётчик —
 * мои непрочитанные (`unreadCount`).
 */

import type { StaffConversation } from "../../lib/api-chat";
import { chatEventPreview } from "./chat-event-meta";
import { compactOrderNumber, customerName, initialWithSurname, nameWithInitial, rowTimeLabel } from "./chat-format";
import { CHAT_TEXT } from "./chat-texts";

/** Пилюля-пометка: синяя, серая, янтарная. */
export type RowPillTone = "info" | "neutral" | "warning";

export type RowMarker =
  | { kind: "pill"; tone: RowPillTone; text: string }
  | { kind: "leads"; full: string; short: string; mine: boolean }
  | null;

export interface ConversationRowView {
  name: string;
  time: string;
  unread: boolean;
  unreadCount: number;
  /** «Вы: », «Анна К.: » — у клиента пусто. */
  previewAuthor: string;
  preview: string;
  /** Превью — служебная запись (серым). */
  previewIsEvent: boolean;
  /** «Заказ Z-2026-000006» / «Общий чат». */
  context: string;
  /** Сжатый контекст для узкой строки: «Заказ Z-…000006». */
  contextShort: string;
  marker: RowMarker;
}

interface MarkerRule {
  test: (c: StaffConversation) => boolean;
  build: (c: StaffConversation, meUserId: string | null) => RowMarker;
}

/** Пометка строки — первое сработавшее правило. */
const MARKER_RULES: MarkerRule[] = [
  { test: (c) => c.flags.transferredToMe, build: () => ({ kind: "pill", tone: "info", text: CHAT_TEXT.pillTransferred }) },
  { test: (c) => c.flags.unarchivedUnseen, build: () => ({ kind: "pill", tone: "neutral", text: CHAT_TEXT.pillUnarchived }) },
  // Сервер ставит признак только владельцу и оператору (у логиста его нет).
  { test: (c) => c.flags.unassigned, build: () => ({ kind: "pill", tone: "warning", text: CHAT_TEXT.pillUnassigned }) },
  {
    test: (c) => c.assignedUser !== null,
    build: (c, me) => {
      const mine = c.assignedUserId !== null && c.assignedUserId === me;
      const person = c.assignedUser?.name ?? "";
      return mine
        ? { kind: "leads", full: CHAT_TEXT.leadsYou, short: CHAT_TEXT.leadsYou, mine }
        : { kind: "leads", full: CHAT_TEXT.leads(person), short: CHAT_TEXT.leads(initialWithSurname(person)), mine };
    },
  },
];

function marker(c: StaffConversation, meUserId: string | null): RowMarker {
  const rule = MARKER_RULES.find((r) => r.test(c));
  return rule ? rule.build(c, meUserId) : null;
}

/** Автор перед последним сообщением. */
function authorPrefix(c: StaffConversation): string {
  const last = c.lastMessage;
  if (!last || last.authorType !== "STAFF") return "";
  if (last.isMine) return `${CHAT_TEXT.you}: `;
  return last.author?.name ? `${nameWithInitial(last.author.name)}: ` : "";
}

/** Последнее событие — запись новее последнего сообщения? */
function eventIsLatest(c: StaffConversation): boolean {
  if (!c.lastEvent) return false;
  if (!c.lastMessage) return true;
  return Date.parse(c.lastEvent.at) > Date.parse(c.lastMessage.at);
}

function contextLabel(c: StaffConversation, compact: boolean): string {
  if (c.contextOrder) return CHAT_TEXT.order(compact ? compactOrderNumber(c.contextOrder.number) : c.contextOrder.number);
  if (c.contextDeal) return `Сделка ${c.contextDeal.displayName}`;
  return CHAT_TEXT.generalChat;
}

export function conversationRowView(
  c: StaffConversation,
  meUserId: string | null,
  now: Date = new Date(),
): ConversationRowView {
  const eventLatest = eventIsLatest(c);
  let preview: string;
  if (eventLatest && c.lastEvent) preview = chatEventPreview(c.lastEvent, meUserId);
  else preview = c.lastMessage?.preview ?? c.lastMessagePreview ?? CHAT_TEXT.noMessages;
  return {
    name: customerName(c.customer),
    time: rowTimeLabel(c.lastActivityAt || c.createdAt, now),
    unread: c.unreadCount > 0,
    unreadCount: c.unreadCount,
    previewAuthor: eventLatest ? "" : authorPrefix(c),
    preview,
    previewIsEvent: eventLatest || (!c.lastMessage && !c.lastMessagePreview),
    context: contextLabel(c, false),
    contextShort: contextLabel(c, true),
    marker: marker(c, meUserId),
  };
}
